import { useLocation, useParams } from "react-router-dom";
import type { InboxView } from "../../../types";
import type { InboxThread } from "@tellescope/types-models";

// Define valid inbox views locally to avoid circular dependency with routing
const VALID_INBOX_VIEWS: InboxView[] = ["inbox", "drafts-sent", "archive", "trash"];

const convertStringToInboxView = (str: string): InboxView | undefined => {
  // @ts-expect-error string is not narrow enough type
  if (VALID_INBOX_VIEWS.includes(str)) {
    return str as InboxView;
  }
  return undefined;
};

const VALID_CHANNELS = [
  "Email",
  "GroupMMS",
  "Chat",
  "Phone",
  "SMS",
  "Zendesk",
] as const satisfies InboxThread["type"][];
const LOWER_VALID_CHANNELS = VALID_CHANNELS.map((str) => str.toLowerCase()) as Lowercase<InboxThread["type"]>[];

const convertStringToChannel = (str: string): InboxThread["type"] | undefined => {
  const matchIndex = VALID_CHANNELS.findIndex((item) => str === item);
  const lowerMatchIndex = LOWER_VALID_CHANNELS.findIndex((item) => str === item);
  const index = matchIndex === -1 ? lowerMatchIndex : matchIndex;
  if (index === -1) return undefined;

  return VALID_CHANNELS[index];
};

const lowercaseChannel = (channel: InboxThread["type"] | string): Lowercase<InboxThread["type"]> =>
  channel.toLowerCase() as Lowercase<InboxThread["type"]>;

export interface ParsedInboxRoute {
  view: InboxView;
  channel?: {
    route: Lowercase<InboxThread["type"]>;
    original: InboxThread["type"];
  };
  threadId?: string;
}

export const useParsedInboxRoute = (): ParsedInboxRoute | undefined => {
  const location = useLocation();
  const params = useParams();

  const segments = location.pathname.split("/");

  const messagingRootSegmentIndex = segments.findIndex((seg) => seg === "messaging");
  if (messagingRootSegmentIndex === -1) return undefined;

  const viewSegment = segments[messagingRootSegmentIndex + 1];
  const view = viewSegment ? convertStringToInboxView(viewSegment) : undefined;
  if (!view) {
    return undefined;
  }

  if (segments.length <= messagingRootSegmentIndex + 2) {
    return {
      view,
    };
  }

  const channelSegment = segments[messagingRootSegmentIndex + 2];
  const originalChannel = channelSegment ? convertStringToChannel(channelSegment) : undefined;

  if (!originalChannel) {
    return {
      view,
    };
  }

  if (!params.threadId) {
    return {
      view,
      channel: {
        original: originalChannel,
        route: lowercaseChannel(originalChannel),
      },
    };
  }

  return {
    view,
    channel: {
      original: originalChannel,
      route: lowercaseChannel(originalChannel),
    },
    threadId: params.threadId,
  };
};
